import { useFlags } from 'launchdarkly-react-client-sdk';
import { Flex, ThemeUICSSObject } from 'theme-ui';

import { OrderLine, RefrigerationState } from 'src/graphql/types/delivery';
import { GDeliveryStatusDmp } from 'src/graphql/types/generated';
import { getQuantityStr, getUnitWeightLb } from './PackingList/utils';
import { useAppSelector } from '../hooks/store';
import {
  deliveryPrimaryStatusesSelector,
  deliveryStatusesSelector,
} from '../state/selectors/delivery';
import { EditDeliveredQuantityButton } from './EditDeliveredQuantity';
import HighlightedText from './HighlightedText';
import FreezerRequiredIcon from './Icons/FreezerRequiredIcon';
import RefrigeratedDairyIcon from './Icons/RefrigeratedDairyIcon';
import RefrigerationRequiredIcon from './Icons/RefrigerationRequiredIcon';
import Tag from './Tag';

const itemDetailsTitleContainer: ThemeUICSSObject = {
  display: 'flex',
  fontFamily: 'body',
  variant: 'text.labelMd',
  color: 'secondaryText',
};

const itemDetailsFooterContainer: ThemeUICSSObject = {
  display: 'flex',
  paddingTop: '8px',
};

const itemDetailsItemName: ThemeUICSSObject = {
  paddingTop: '4px',
  maxWidth: '280px',
};

const itemDetailsItemType: ThemeUICSSObject = {
  fontFamily: 'body',
  variant: 'text.labelSm',
  color: 'secondaryText',
};

const itemDetailsQuantityDelivered: ThemeUICSSObject = {
  fontFamily: 'body',
  variant: 'text.labelSm',
  color: 'secondaryText',
  textAlign: 'right',
  marginLeft: 'auto',
};

const itemDetailsQuantityFulfilled: ThemeUICSSObject = {
  fontFamily: 'body',
  variant: 'text.labelSm',
  color: 'secondaryText',
  textAlign: 'right',
  marginLeft: 'auto',
  display: 'inline-block',
};

export type TagStyleModel = {
  itemLevelTraking: boolean;
  isStopVisited: boolean;
  isUndelivered: boolean;
  isPartiallyDelivered: boolean;
  deliveredQuantity: number;
  orderUnitQuantity: number;
};

const getTagStyle = (model: TagStyleModel): React.CSSProperties => {
  return (model.itemLevelTraking && model.isUndelivered) ||
    (model.itemLevelTraking &&
      (model.isStopVisited || model.isPartiallyDelivered) &&
      model.deliveredQuantity < model.orderUnitQuantity)
    ? { backgroundColor: 'errorBackgroundWeak' }
    : {};
};

const getWeightDisplay = (orderLine: OrderLine): string => {
  if (!orderLine.isMarketPriced && !orderLine.isPounds) return '';
  const unitWeight = orderLine.isMarketPriced
    ? orderLine.unitWeightLb
    : getUnitWeightLb(orderLine);
  const totalWeight = orderLine.isMarketPriced
    ? orderLine.totalWeightLb
    : orderLine.orderedWeightLb;
  if (unitWeight && totalWeight)
    return `${unitWeight} lb/unit | ${totalWeight} lb total`;
  if (totalWeight) return `${totalWeight} lb`;
  return '';
};

export const OrderLineInfo = ({
  itemLevelTraking = false,
  isStopVisited = false,
  orderLine,
  isFulfilled = true,
  stopId,
  orderId,
  editable = true,
  searchValue,
}: {
  itemLevelTraking?: boolean;
  isStopVisited?: boolean;
  orderLine: OrderLine;
  isFulfilled?: boolean;
  stopId: string;
  orderId: string;
  editable?: boolean;
  searchValue?: string;
}): JSX.Element | null => {
  // Uses primary statuses only
  const { deliveryServiceSeparatePrimaryStatusState } = useFlags();
  const deliveryAllStatuses = useAppSelector(deliveryStatusesSelector);
  const deliveryPrimaryStatuses = useAppSelector(
    deliveryPrimaryStatusesSelector,
  );
  const deliveryStatuses = deliveryServiceSeparatePrimaryStatusState
    ? deliveryPrimaryStatuses
    : deliveryAllStatuses;
  const myStopStatus = deliveryStatuses.stopStatuses.find(
    (stopStatus) => stopStatus.stopId === stopId,
  );

  const itemName = orderLine.sku?.name;
  const deliveredQuantity = orderLine.deliveredQuantity ?? 0;
  const orderUnitQuantity = orderLine.orderUnitQuantity;
  const fulfillmentQuantity = orderLine.fulfillmentQuantity ?? 0;

  // Bail on whole order line as it's not part of the order/delivery since it was zeroed out
  if (!orderUnitQuantity) return null;

  const showQuantityDelivered =
    !itemLevelTraking || (itemLevelTraking && isStopVisited);
  const quantityDelivered = itemLevelTraking
    ? deliveredQuantity + ' delivered'
    : 'Qty:' + orderUnitQuantity;
  const quantity = getQuantityStr(orderLine);

  const fulfillmentQuantityStyle =
    itemLevelTraking && !isFulfilled
      ? { color: 'secondaryBackgroundActive' }
      : {};
  const tagStyle = getTagStyle({
    itemLevelTraking,
    isStopVisited,
    isUndelivered: myStopStatus?.stopStatus === GDeliveryStatusDmp.Undelivered,
    isPartiallyDelivered:
      myStopStatus?.stopStatus === GDeliveryStatusDmp.PartiallyDelivered,
    deliveredQuantity,
    orderUnitQuantity,
  });
  const weightDisplay = getWeightDisplay(orderLine);

  const refrigerationState = orderLine.sku?.refrigerationState;
  const isRefrigerated = refrigerationState === RefrigerationState.refrigerated;
  const isFrozen = refrigerationState === RefrigerationState.frozen;
  const isDairy = orderLine.sku?.subcategoryName === 'Dairy';

  return (
    <div id={`item-${orderLine.id}`}>
      <div sx={itemDetailsTitleContainer}>
        <div id="item-name" sx={itemDetailsItemName}>
          <HighlightedText text={itemName} highlight={searchValue} />
        </div>
        {showQuantityDelivered && (
          <Flex sx={itemDetailsQuantityDelivered}>
            <Tag text={quantityDelivered} style={tagStyle} />
            {itemLevelTraking && editable && (
              <EditDeliveredQuantityButton
                orderId={orderId}
                orderLineId={orderLine.id}
                quantityDelivered={deliveredQuantity}
                itemName={itemName}
                orderUnitQuantity={orderUnitQuantity}
              />
            )}
          </Flex>
        )}
      </div>
      <div sx={itemDetailsFooterContainer}>
        <Flex>
          <div id="item-type" sx={itemDetailsItemType}>
            {quantity}
          </div>
          {isRefrigerated && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <RefrigerationRequiredIcon
                testId={orderLine.id}
                style={{ marginLeft: '8px' }}
              />
              {isDairy && (
                <RefrigeratedDairyIcon
                  testId={orderLine.id}
                  style={{ paddingTop: '1px' }}
                />
              )}
            </div>
          )}
          {isFrozen && (
            <FreezerRequiredIcon
              testId={orderLine.id}
              style={{ marginLeft: '8px' }}
            />
          )}
          {weightDisplay && (
            <div
              id="item-weight"
              sx={{ ...itemDetailsItemType, marginLeft: '8px' }}
            >
              {weightDisplay}
            </div>
          )}
        </Flex>
        {itemLevelTraking && (
          <div id="fulfillment-quantity" sx={itemDetailsQuantityFulfilled}>
            <span id="fulfillment-quantity-text" sx={fulfillmentQuantityStyle}>
              {fulfillmentQuantity} fulfilled
            </span>{' '}
            · {orderUnitQuantity} ordered
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderLineInfo;
